import {
  HTMLAttributes,
  KeyboardEventHandler,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
} from 'react';

import {
  getShouldRenderCount,
  useForwardedRef,
  useMeasure,
} from '@adsk/alloy-react-helpers';
import { RenderIconType } from '@adsk/alloy-react-icon';

import { LEFT_PADDING, RIGHT_PADDING } from '../consts';
import { firefoxNumberInputWorkaround, getIsFirefox } from '../helpers';
import { getSideElementPadding } from '../helpers/sideElementPadding';
import { TextInputType } from '../types';

const PADDING_OFFSET_RIGHT = 8;
const PADDING_OFFSET_LEFT = 5;

type UseTextInputProps = {
  type?: TextInputType;
  renderLeft?: RenderIconType;
  renderRight?: RenderIconType;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  selectTextOnFocus?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  ref?: React.ForwardedRef<HTMLInputElement>;
  showCount?: boolean;
  softLimit?: number;
  maxLength?: number;
  showCountThreshold?: number;
  value?: string;
  defaultValue?: string | number | readonly string[];
};

export default ({
  type,
  renderLeft,
  renderRight,
  onFocus,
  selectTextOnFocus,
  onKeyDown,
  ref,
  showCount,
  softLimit,
  maxLength,
  showCountThreshold,
  onChange,
  value,
  defaultValue,
}: UseTextInputProps) => {
  const inputRef = useForwardedRef<HTMLInputElement | null>(ref || null);

  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [paddingLeft, setPaddingLeft] = useState(LEFT_PADDING);
  const [paddingRight, setPaddingRight] = useState(RIGHT_PADDING);
  const divRef = useCallback((element: HTMLDivElement | null) => {
    if (!element) {
      return;
    }

    const divStyle = getComputedStyle(element);

    if (divStyle.paddingLeft) {
      setPaddingLeft(parseInt(divStyle.paddingLeft));
    }

    if (divStyle.paddingRight) {
      setPaddingRight(parseInt(divStyle.paddingRight));
    }
  }, []);

  const { width: leftWidth } = useMeasure(leftRef);
  const { width: rightWidth } = useMeasure(rightRef);

  useLayoutEffect(() => {
    // left padding
    if (inputRef.current) {
      const padding = getSideElementPadding({
        hasRenderer: !!renderLeft,
        sideElementWidth: leftWidth,
        padding: paddingLeft,
        inputRef,
        sideElementRef: leftRef,
        paddingOffset: leftWidth > 0 ? PADDING_OFFSET_LEFT : 0,
      });

      inputRef.current.style.paddingLeft = `${padding}px`;
    }
  }, [inputRef, renderLeft, leftWidth, paddingLeft]);

  useLayoutEffect(() => {
    if (value !== undefined && value !== null) {
      setCharCount(value.length);
    }
  }, [value]);

  useLayoutEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setCharCount(defaultValue.toString().length);
    }
    // we only want to do this on mount since it's the default value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (getIsFirefox() && type === 'number') {
        firefoxNumberInputWorkaround({ event, inputRef });
      }

      onKeyDown?.(event);
    },
    [inputRef, onKeyDown, type],
  );

  const shouldRenderCount = useMemo(
    () =>
      getShouldRenderCount({
        showCount,
        maxLength,
        softLimit,
        charCount,
        showCountThreshold,
      }),

    [showCount, maxLength, softLimit, charCount, showCountThreshold],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (selectTextOnFocus) {
        event.target.select();
      }
      onFocus?.(event);
    },
    [onFocus, selectTextOnFocus],
  );

  useLayoutEffect(() => {
    // right padding
    if (inputRef.current) {
      const padding = getSideElementPadding({
        hasRenderer: !!(renderRight ?? shouldRenderCount),
        sideElementWidth: rightWidth,
        padding: paddingRight,
        inputRef,
        sideElementRef: rightRef,
        paddingOffset: PADDING_OFFSET_RIGHT,
      });

      inputRef.current.style.paddingRight = `${padding}px`;
    }
  }, [inputRef, renderRight, shouldRenderCount, rightWidth, paddingRight]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (value === undefined || value === null) {
        if (event.target.value) {
          setCharCount(event.target.value.length);
        } else {
          setCharCount(0);
        }
      }

      onChange?.(event);
    },
    [onChange, value],
  );

  const getInputProps: () => HTMLAttributes<HTMLInputElement> =
    useCallback(() => {
      return {
        ref: inputRef,
        type,
        onFocus: handleFocus,
        onKeyDown: handleKeyDown,
        onChange: handleChange,
        dir: 'auto',
        value,
        defaultValue,
        style: { paddingLeft, paddingRight },
      };
    }, [
      inputRef,
      type,
      handleFocus,
      handleKeyDown,
      handleChange,
      value,
      defaultValue,
      paddingLeft,
      paddingRight,
    ]);

  return {
    refs: {
      divRef,
      leftRef,
      rightRef,
    },
    propGetters: {
      getInputProps,
    },
    padding: { paddingLeft, paddingRight },
    charCount,
    shouldRenderCount,
  };
};
